import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { motion, useTransform, useViewportScroll } from "framer-motion";
import { useWindowSize } from "../../../../hooks/windowSize";
import React, { useEffect, useRef, useState } from "react";

interface TableBlockProps {
  subHeadOne: String;
  subHeadTwo: String;
  subHeadThree: String;
  bodyOne: String;
  bodyTwo: String;
  bodyThree: String;
  color:
    | "inherit"
    | "initial"
    | "primary"
    | "secondary"
    | "textPrimary"
    | "textSecondary"
    | "error";
  bgColor: String;
  bgImg?: String;
  bgPositionDesktop?: String;
  bgPositionMobile?: String;
}

const useStyles = makeStyles((theme) => ({
  mainWrapperColCenter: (props: TableBlockProps) => ({
    backgroundColor: `${props.bgColor}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "space-between",
    height: "75vh",
    backgroundRepeat: "no-repeat",
    backgroundPosition: `${props.bgPositionDesktop}`,
    backgroundSize: "cover",
    backgroundImage: `linear-gradient(75deg, rgba(31,40,51,0.5) 40%, rgba(31,40,51,0) 75%),url(${props.bgImg})`,
    padding: theme.spacing(16),
    gap: "24px",
    [theme.breakpoints.down("lg")]: {
      padding: theme.spacing(10),
    },
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(10),
    },
    [theme.breakpoints.down("sm")]: {
      backgroundPosition: `${props.bgPositionMobile}`,
      height: "75vh",
      padding: theme.spacing(5),
    },
    [theme.breakpoints.down("xs")]: {
      height: "75vh",
      paddingTop: theme.spacing(5),
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(5),
      paddingLeft: theme.spacing(4),
    },
  }),
  headlineWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    height: "100%",
    marginBottom: theme.spacing(0),
  },
  rowColumnWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: "0px",
    width: "100%",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  rowWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-end",
    gap: "4px",
    paddingLeft: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    marginBottom: theme.spacing(0),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "row",
    },
  },
  jumbo: {
    fontSize: "80px !important",
    lineHeight: "80px !important",
    letterSpacing: -2,
    color: "#ffffff",
    [theme.breakpoints.down("xs")]: {
      fontSize: "70px !important",
      letterSpacing: -2,
      lineHeight: "70px !important",
    },
  },
}));

export const TableBlock: React.FC<TableBlockProps> = (props) => {
  const {
    subHeadOne,
    subHeadTwo,
    subHeadThree,
    bodyOne,
    bodyTwo,
    bodyThree,
    color,
  } = props;
  const classes = useStyles(props);

  const size = useWindowSize();
  const lineOneRef = useRef(null);
  const numbersOneRef = useRef(null);
  const lineTwoRef = useRef(null);
  const lineThreeRef = useRef(null);
  const { scrollY } = useViewportScroll();
  const [lineOneYPosition, setlineOneYPosition] = useState<number>(0);
  const [lineTwoYPosition, setlineTwoYPosition] = useState<number>(0);
  const [lineThreeYPosition, setlineThreeYPosition] = useState<number>(0);

  useEffect(() => {
    if (lineOneRef.current && lineTwoRef.current && lineThreeRef.current) {
      setlineOneYPosition(lineOneRef.current.offsetTop);
      setlineTwoYPosition(lineTwoRef.current.offsetTop);
      setlineThreeYPosition(lineThreeRef.current.offsetTop);
    }
  }, [lineOneRef.current, lineTwoRef.current, lineThreeRef.current]);

  const lineOneTransform = useTransform(
    scrollY,
    [
      lineOneYPosition - size.innerHeight * 0.9,
      lineOneYPosition - size.innerHeight * 0.5,
    ],
    ["0%", "25%"]
  );

  const lineTwoTransform = useTransform(
    scrollY,
    [
      lineTwoYPosition - size.innerHeight * 0.94,
      lineTwoYPosition - size.innerHeight * 0.55,
    ],
    ["0%", "45%"]
  );
  const lineThreeTransform = useTransform(
    scrollY,
    [
      lineThreeYPosition - size.innerHeight * 0.98,
      lineThreeYPosition - size.innerHeight * 0.6,
    ],
    ["0%", "100%"]
  );

  return (
    <Grid item className={classes.mainWrapperColCenter} xs={12} md={6}>
      <Box className={classes.headlineWrapper}>
        <Box className={classes.rowColumnWrapper}>
          <motion.div
            style={{
              width: lineOneTransform,
              backgroundColor: "white",
              overflow: "visible",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "flex-end",
              whiteSpace: "nowrap",
            }}
            ref={lineOneRef}
          >
            <Box className={classes.rowWrapper}>
              <Typography variant="h4" color={color}>
                {subHeadOne}
              </Typography>
            </Box>
          </motion.div>

          <Typography variant="h1" className={classes.jumbo} color={color}>
            {bodyOne}
          </Typography>
        </Box>

        <Box className={classes.rowColumnWrapper}>
          <motion.div
            style={{
              width: lineTwoTransform,
              backgroundColor: "white",
              overflow: "visible",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "flex-end",
              whiteSpace: "nowrap",
            }}
            ref={lineTwoRef}
          >
            <Box className={classes.rowWrapper}>
              <Typography variant="h4" color={color}>
                {subHeadTwo}
              </Typography>
            </Box>
          </motion.div>
          <Typography variant="h1" className={classes.jumbo} color={color}>
            {bodyTwo}
          </Typography>
        </Box>
        <Box className={classes.rowColumnWrapper}>
          <motion.div
            style={{
              width: lineThreeTransform,
              backgroundColor: "white",
              overflow: "visible",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "flex-end",
              whiteSpace: "nowrap",
            }}
            ref={lineThreeRef}
          >
            <Box className={classes.rowWrapper}>
              <Typography variant="h4" color={color}>
                {subHeadThree}
              </Typography>
            </Box>
          </motion.div>
          <Typography variant="h1" className={classes.jumbo} color={color}>
            {bodyThree}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};
